/// <amd-module name="RMWidgets/DeploymentStatusWidget/components/DeploymentStatusWidget" />
import * as React from "react";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { format, localeFormat } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Table, ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { Icon } from "VSSUI/Icon";
import { Status, IStatusProps, StatusSize } from "VSSUI/Status";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Tooltip } from "VSSUI/TooltipEx";

import { WidgetProps, LoadingStatus } from "Dashboards/WidgetContracts/WidgetProps";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";

import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";
import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";

import { createBuildResultsUrl } from "Release/Helpers/Link";

import { DSWState } from "RMWidgets/DeploymentStatusWidget/DSWState";
import { DSWDataProvider } from "RMWidgets/DeploymentStatusWidget/DSWDataProvider";
import {
    IDSWBuildInfo,
    IDSWEnvironmentInfo,
    IDSWDeploymentInfo,
    IDSWTestResultInfo,
    IDSWLastColumnInfo,
    DSWLastColumnType,
    DSWTooltip,
    DSWViewType,
    IDSWCustomSettings
} from "RMWidgets/DeploymentStatusWidget/DSWContracts";
import { DSWColumnKeys } from "RMWidgets/DeploymentStatusWidget/DSWConstants";
import { CellToolTip } from "RMWidgets/DeploymentStatusWidget/components/CellToolTip";
import * as Resources from "RMWidgets/DeploymentStatusWidget/Resources";

export class DeploymentStatusWidget extends VssComponent<WidgetProps, DSWState> {
    private parsedSettings: IDSWCustomSettings;

    public static componentType = "deploymentStatusWidget";
    private static MaxBuildNameLength: number = 9;

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props, context);
        this.initializeState();
    }

    async componentDidMount() {
        await this.getDataAndSetState();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    async componentDidUpdate(prevProps: WidgetProps) {
        if (prevProps.settings !== this.props.settings || prevProps.size !== this.props.size) {
            await this.getDataAndSetState();
        }
    }

    async getDataAndSetState() {
        try {
            if (this.props.settings) {
                this.parsedSettings = JSON.parse(this.props.settings);
                if (this.areSettingsValid()) {
                    const dataProvider = new DSWDataProvider(this.context.pageContext);
                    const state = await dataProvider.getDSWStateData(this.parsedSettings, this.props.size);
                    this.setState(state);
                }
            }

            this.props.onWidgetLoaded();
        } catch (error) {
            this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error })
        }
    }

    private areSettingsValid(): boolean {
        return this.parsedSettings &&
            this.parsedSettings.buildDefinitionId > 0 &&
            this.parsedSettings.environments &&
            this.parsedSettings.environments.length > 0 &&
            !this.areRuleBoundariesInvalid()
    }

    private areRuleBoundariesInvalid(): boolean {

        if (DSWViewType.checkViewType(this.parsedSettings.viewType,
                DSWViewType.DeploymentStatus,
                this.parsedSettings.viewTypeIdentifier,
                DSWViewType.DeploymentStatusIdentifier)) {
            return false;
        }

        if (!this.parsedSettings.ruleBoundariesErrors) {
            return true;
        }

        let isInvalid = false;

        for (let index in this.parsedSettings.ruleBoundariesErrors) {
            isInvalid = isInvalid || this.parsedSettings.ruleBoundariesErrors[index];
        }

        return isInvalid;
    }

    public render(): JSX.Element | undefined {
        if (!this.props.settings || !this.areSettingsValid()) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        }

        if (!this.hasBuilds() && !!this.state.buildDefinitionInfo) {
            return <NoDataMessage
                visualTitle={this.props.name}
                size={this.props.size}
                cardId={2}
            />;
        } else {
            return (
                <div className="dsw-widget-container">
                    <VisualTitle text={this.props.name} />
                    <div className="dsw-table-container">
                        {
                            this.hasEnvironments() &&
                            <Table
                                className={"dsw-table"}
                                columns={this.getColumnInfo()}
                                showLines={false}
                                itemProvider={new ArrayItemProvider(this.state.environmentsInfo)}
                            />
                        }
                    </div>
                    {this.renderDSWFooter()}
                </div>
            );
        }
    }

    /**
     * Footer section
     */
    private renderDSWFooter(): JSX.Element {
        return (
            <div className="dsw-footer">
                {this.renderBranchInfo()}
                {this.renderBuildDefinitionInfo()}
            </div>);
    }

    private renderBranchInfo(): JSX.Element {
        return (
            <div className="branch-info">
                <Icon iconName={"OpenSource"} className={"branch-info-icon"} />
                <label className="branch-label">{Resources.BranchLabel}</label>
                <Tooltip
                    text={this.state.branchName}
                    delayMs={500}
                >
                    <span aria-label={`${Resources.BranchLabel} ${this.state.branchName}`} className="branch-name">{this.state.branchName}</span>
                </Tooltip>
            </div>);
    }

    private renderBuildDefinitionInfo(): JSX.Element {
        return (
            <div className="build-definition-info-cont">
                <div className="build-definition-info">
                    <Icon iconName={"Build"} />
                    <label className="build-definition-label">{Resources.BuildDefinitionLabel}</label>
                    {
                        (this.state.buildDefinitionInfo) &&
                        <div className={"build-definition-text-container"}>
                            <Tooltip
                                className={"build-definition-tooltip"}
                                text={this.state.buildDefinitionInfo ? this.state.buildDefinitionInfo.name : ""}
                                delayMs={500}
                            >
                                <FPSLink
                                    ariaLabel={`${Resources.BuildDefinitionLabel} ${this.state.buildDefinitionInfo.name}`}
                                    className={"dsw-build-definition-info-link"}
                                    href={this.state.buildDefinitionInfo.url}
                                    target={this.props.openLinksInNewTab ? "_blank" : undefined}
                                >
                                    {this.state.buildDefinitionInfo.name}
                                </FPSLink>
                            </Tooltip>
                        </div>
                    }
                </div>
            </div>
        );
    }

    private hasBuilds(): boolean {
        return this.state.builds
            && this.state.builds.length > 0;
    }

    private hasEnvironments(): boolean {
        return this.state.environmentsInfo
            && this.state.environmentsInfo.length > 0;
    }

    /**
     * Table column info
     */
    private getColumnInfo(): ITableColumn<IDSWEnvironmentInfo>[] {
        let columns: ITableColumn<IDSWEnvironmentInfo>[] = [];

        columns.push({
            id: DSWColumnKeys.Environments,
            name: Resources.DSWEnvironmentsHeader,
            className: "dsw-cell row-header",
            headerClassName: "dsw-header-cell",
            width: -40,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IDSWEnvironmentInfo>,
                environmentInfo: IDSWEnvironmentInfo
            ): JSX.Element => this.renderEnvironmentCell(columnIndex, tableColumn, environmentInfo)
        });

        this.state.builds.forEach((build: IDSWBuildInfo) => {
            columns.push({
                headerClassName: "build-name",
                className: "dsw-cell",
                id: build.id.toString(),
                name: this.getTruncatedBuildName(build.name),
                width: -11,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IDSWEnvironmentInfo>,
                    environmentInfo: IDSWEnvironmentInfo
                ): JSX.Element => this.renderDSWCell(columnIndex, tableColumn, build, environmentInfo),
                renderHeaderCell: (
                    index: number,
                    column: ITableColumn<IDSWEnvironmentInfo>,
                    focusZoneId: string | undefined): JSX.Element => this.renderDSWHeaderCell(column, build.id)
            });
        });

        if (this.parsedSettings.lastColumnType === DSWLastColumnType.LastDeployed) {
            columns.push({
                headerClassName: "dsw-last-column-header",
                className: "dsw-cell dsw-last-column-cell",
                id: DSWColumnKeys.LastDeployed,
                name: Resources.DSWLastDeployed,
                width: -22,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IDSWEnvironmentInfo>,
                    environmentInfo: IDSWEnvironmentInfo
                ): JSX.Element => this.renderLastColumnCell(columnIndex, tableColumn, environmentInfo)
            });
        }

        if (this.parsedSettings.lastColumnType === DSWLastColumnType.FailingSince) {
            columns.push({
                headerClassName: "dsw-last-column-header",
                className: "dsw-cell dsw-last-column-cell",
                id: DSWColumnKeys.FailingSince,
                name: Resources.DSWFailingSince,
                width: -22,
                renderCell: (
                    rowIndex: number,
                    columnIndex: number,
                    tableColumn: ITableColumn<IDSWEnvironmentInfo>,
                    environmentInfo: IDSWEnvironmentInfo
                ): JSX.Element => this.renderLastColumnCell(columnIndex, tableColumn, environmentInfo)
            });
        }

        return columns;
    }

    /**
     * Table header renderer
     */
    private renderDSWHeaderCell = (column: ITableColumn<IDSWEnvironmentInfo>, buildId: number): JSX.Element => {
        const buildName = column.name;
        const buildUrl = createBuildResultsUrl(this.context.pageContext, buildId);

        if (buildName && buildName.length <= DeploymentStatusWidget.MaxBuildNameLength) {
            return <td className={column.headerClassName} data-item-key={column.id} key={column.id}>
                <FPSLink
                    href={buildUrl}
                    target={this.props.openLinksInNewTab ? "_blank" : undefined}
                    data-is-focusable="true"
                    rel={"noopener noreferrer"}>
                    <span className={"dsw-build-name-span"}>{buildName}</span>
                </FPSLink>
            </td>;
        }

        // Build column header if the build name exceeds column width
        return <td className={column.headerClassName} data-item-key={column.id} key={column.id}>
            <FPSLink
                href={buildUrl}
                target={this.props.openLinksInNewTab ? "_blank" : undefined}
                className="build-name-link"
                data-is-focusable="true"
                rel={"noopener noreferrer"}>
                <Tooltip
                    className={"build-name-tooltip"}
                    text={buildName}
                    delayMs={500}
                >
                    <span className={"dsw-build-name-span"}>{this.getTruncatedBuildName(buildName)}</span>
                </Tooltip>
            </FPSLink>
        </td>;
    }

    /** 
     * Table cell renderer - Environment info cell 
     */
    private renderEnvironmentCell(
        columnIndex: number,
        tableColumn: ITableColumn<IDSWEnvironmentInfo>,
        environmentInfo: IDSWEnvironmentInfo
    ): JSX.Element {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={`col-${columnIndex}`}
            >
                <Tooltip
                    renderContent={() => this.constructMultiLineToolTip(environmentInfo.tooltip)}
                    className={"env-name-tooltip"}
                    delayMs={500}
                >
                    <FPSLink
                        ariaLabel={localeFormat(Resources.EnvironmentInfoAriaLabel, environmentInfo.tooltip[0], environmentInfo.tooltip[1])}
                        className="env-name"
                        href={environmentInfo.environmentSummaryUrl}
                        target={this.props.openLinksInNewTab ? "_blank" : undefined}
                        rel={"noopener noreferrer"}>
                        {environmentInfo.definitionEnvironmentName}
                    </FPSLink>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    /**
     * Table cell renderer - Deployment Status cell
     */
    private renderDSWCell(
        columnIndex: number,
        tableColumn: ITableColumn<IDSWEnvironmentInfo>,
        buildInfo: IDSWBuildInfo,
        environmentInfo: IDSWEnvironmentInfo
    ): JSX.Element {
        let deploymentInfo: IDSWDeploymentInfo | undefined;
        let testResultsInfo: IDSWTestResultInfo;
        let tooltip: DSWTooltip = {};
        tooltip.build = buildInfo.tooltip.build;
        tooltip.buildStatus = buildInfo.tooltip.buildStatus;
        let passPercentage: string = "";
        let statusColourCss: string = "";
        let statusProps: IStatusProps | undefined = undefined;
        let url: string = "";

        if (this.state.deploymentsMap
            && this.state.deploymentsMap[environmentInfo.definitionEnvironmentId]
            && this.state.deploymentsMap[environmentInfo.definitionEnvironmentId][buildInfo.id]) {

            deploymentInfo = this.state.deploymentsMap[environmentInfo.definitionEnvironmentId][buildInfo.id];

            if (DSWViewType.checkViewType(this.parsedSettings.viewType,
                    DSWViewType.DeploymentStatus,
                    this.parsedSettings.viewTypeIdentifier,
                    DSWViewType.DeploymentStatusIdentifier)) {
                statusColourCss = deploymentInfo.statusColourCss;
                statusProps = deploymentInfo.statusProps;
            } else {
                statusColourCss = "cell-status-na";
                statusProps = undefined;
            }

            url = deploymentInfo.releaseSummaryUrl;

            if (deploymentInfo.toolTip.release) {
                tooltip.release = deploymentInfo.toolTip.release;
            }

            if (deploymentInfo.toolTip.environment) {
                tooltip.environment = deploymentInfo.toolTip.environment;
            }

            if (deploymentInfo.toolTip.deploymentStatus) {
                tooltip.deploymentStatus = deploymentInfo.toolTip.deploymentStatus;
            }

            if (deploymentInfo.toolTip.deploymentDuration) {
                tooltip.deploymentDuration = deploymentInfo.toolTip.deploymentDuration;
            }
        }

        if (deploymentInfo
            && this.state.testResultsMap
            && this.state.testResultsMap[deploymentInfo.releaseEnvironmentId]) {

            testResultsInfo = this.state.testResultsMap[deploymentInfo.releaseEnvironmentId];
            url = testResultsInfo.testResultsUrl;
            passPercentage = testResultsInfo.passPercentage;

            if (testResultsInfo.toolTip.testsPassed) {
                tooltip.testsPassed = testResultsInfo.toolTip.testsPassed;
            }

            if (testResultsInfo.toolTip.testRunDuration) {
                tooltip.testRunDuration = testResultsInfo.toolTip.testRunDuration;
            }

            if (testResultsInfo.toolTip.abortedRuns) {
                tooltip.abortedRuns = testResultsInfo.toolTip.abortedRuns;
            }

            if (DSWViewType.checkViewType(this.parsedSettings.viewType,
                    DSWViewType.TestPassRate,
                    this.parsedSettings.viewTypeIdentifier,
                    DSWViewType.TestPassRateIdentifier)) {
                statusProps = testResultsInfo.testStatusProps;
                statusColourCss = testResultsInfo.testStatusColor;
            }
        }

        let tooltipContent: any = <CellToolTip toolTip={tooltip} />;
        const ariaLabel = this.constructCellAriaLabel(tooltip, passPercentage);

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={`col-${columnIndex}`}
            >
                <Tooltip
                    className={"dsw-cell-tooltip"}
                    renderContent={() => { return tooltipContent; }}
                    delayMs={500}
                >
                    <div className={`cell-container ${statusColourCss}`}>
                        <FPSLink
                            ariaLabel={ariaLabel}
                            href={url}
                            target={this.props.openLinksInNewTab ? "_blank" : undefined}
                            rel={"noopener noreferrer"}
                        >
                            {
                                !!statusProps ?
                                    <Status {...statusProps} className={"status-icon"} size={StatusSize.m} /> : undefined
                            }
                            <div className="test-results">
                                {passPercentage}
                            </div>
                        </FPSLink>
                    </div>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    /** 
     * Table cell renderer - last column cell 
     */
    private renderLastColumnCell(
        columnIndex: number,
        tableColumn: ITableColumn<IDSWEnvironmentInfo>,
        environmentInfo: IDSWEnvironmentInfo
    ): JSX.Element {
        let deploymentInfo: IDSWLastColumnInfo | undefined;
        let testResultsInfo: IDSWTestResultInfo;
        let passPercentage: string = "";
        let statusProps: IStatusProps | undefined = undefined;
        let statusColorCss: string = "cell-status-na";
        let testResultUrl: string = "";
        let buildUrl: string = "";
        let buildName: string = "";
        let tooltip: DSWTooltip = {};

        if (this.state.lastColumnMap
            && this.state.lastColumnMap[environmentInfo.definitionEnvironmentId]) {

            deploymentInfo = this.state.lastColumnMap[environmentInfo.definitionEnvironmentId];

            if (DSWViewType.checkViewType(this.parsedSettings.viewType,
                    DSWViewType.DeploymentStatus,
                    this.parsedSettings.viewTypeIdentifier,
                    DSWViewType.DeploymentStatusIdentifier) ||
                this.parsedSettings.lastColumnType === DSWLastColumnType.FailingSince) {
                statusColorCss = deploymentInfo.statusColourCss || "cell-status-na";
                statusProps = deploymentInfo.statusProps;
            }

            buildUrl = deploymentInfo.buildUrl;
            buildName = deploymentInfo.buildName;
            tooltip.build = buildName;
            tooltip.buildStatus = Resources.DSWBuildStatusCompleted;

            if (deploymentInfo.toolTip.release) {
                tooltip.release = deploymentInfo.toolTip.release;
            }

            if (deploymentInfo.toolTip.environment) {
                tooltip.environment = deploymentInfo.toolTip.environment;
            }

            if (deploymentInfo.toolTip.deploymentStatus) {
                tooltip.deploymentStatus = deploymentInfo.toolTip.deploymentStatus;
            }

            if (deploymentInfo.toolTip.deploymentDuration) {
                tooltip.deploymentDuration = deploymentInfo.toolTip.deploymentDuration;
            }
        }

        if (deploymentInfo
            && this.state.testResultsMap
            && this.state.testResultsMap[deploymentInfo.releaseEnvironmentId]) {

            testResultsInfo = this.state.testResultsMap[deploymentInfo.releaseEnvironmentId];
            passPercentage = testResultsInfo.passPercentage;
            testResultUrl = testResultsInfo.testResultsUrl;

            if (testResultsInfo.toolTip.testsPassed) {
                tooltip.testsPassed = testResultsInfo.toolTip.testsPassed;
            }

            if (testResultsInfo.toolTip.testRunDuration) {
                tooltip.testRunDuration = testResultsInfo.toolTip.testRunDuration;
            }

            if (testResultsInfo.toolTip.abortedRuns) {
                tooltip.abortedRuns = testResultsInfo.toolTip.abortedRuns;
            }

            if (this.parsedSettings.lastColumnType === DSWLastColumnType.LastDeployed &&
                DSWViewType.checkViewType(this.parsedSettings.viewType,
                    DSWViewType.TestPassRate,
                    this.parsedSettings.viewTypeIdentifier,
                    DSWViewType.TestPassRateIdentifier)) {
                statusColorCss = testResultsInfo.testStatusColor;
                statusProps = testResultsInfo.testStatusProps;
            }
        }

        let tooltipContent: any = <CellToolTip toolTip={tooltip} />;
        const deploymentInfoAriaLabel = this.constructCellAriaLabel(tooltip, passPercentage);
        const buildInfoAriaLabel = localeFormat(Resources.BuildInfoAriaLabel, buildName);

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={`col-${columnIndex}`}
            >
                <Tooltip
                    className={"dsw-cell-tooltip"}
                    renderContent={() => { return tooltipContent; }}
                    delayMs={500}
                >
                    <div className={`last-deployment-cell-cont ${statusColorCss}`}>
                        <div className={"last-deployment-info"}>
                            {
                                !!statusProps ?
                                    <Status {...statusProps} className={"status-icon"} size={StatusSize.m} /> : undefined
                            }
                            {
                                testResultUrl &&
                                <FPSLink
                                    ariaLabel={deploymentInfoAriaLabel}
                                    href={testResultUrl}
                                    target={this.props.openLinksInNewTab ? "_blank" : undefined}
                                    rel={"noopener noreferrer"}>
                                    <span className="last-deployment-test-results">
                                        {passPercentage}
                                    </span>
                                </FPSLink>
                            }
                        </div>
                        <div className={"last-deployment-build-info"}>
                            <FPSLink
                                ariaLabel={buildInfoAriaLabel}
                                className="last-dep-build-link"
                                href={buildUrl}
                                target={this.props.openLinksInNewTab ? "_blank" : undefined}
                                rel={"noopener noreferrer"}>
                                <span className="last-dep-build-name">
                                    {this.getTruncatedBuildName(buildName)}
                                </span>
                            </FPSLink>
                        </div>
                    </div>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private constructMultiLineToolTip(toolTip: string[]): JSX.Element {
        const releaseDefinition = localeFormat(Resources.DeploymentStatusWidgetReleaseToolTip, toolTip[0]);
        const environment = localeFormat(Resources.DeploymentStatusWidgetEnvironmentNameToolTip, toolTip[1]);
        let toolTipContent = `${releaseDefinition}\n${environment}`;

        return <span className={"dsw-tooltip-multi-line"}>{toolTipContent}</span>;
    }

    private constructCellAriaLabel(toolTip: DSWTooltip, passPercentage: string): string {
        let ariaLabel = "";

        if (toolTip.release && toolTip.environment && toolTip.deploymentStatus && toolTip.deploymentDuration) {
            ariaLabel = format(Resources.DeploymentAriaLabel, toolTip.build, toolTip.environment, toolTip.deploymentStatus);
        }

        if (toolTip.testsPassed && toolTip.testRunDuration) {
            ariaLabel += format(Resources.TestResultsAriaLabel, passPercentage);
        }

        return ariaLabel;
    }

    // Returns a truncated build name since we can only show limited characters in the column header.
    private getTruncatedBuildName(buildName: string | undefined): string | undefined {
        const maxLength: number = DeploymentStatusWidget.MaxBuildNameLength;
        const ellipsisLength: number = 3;

        if (!buildName || buildName.length <= maxLength) {
            return buildName;
        }

        return `...${buildName.substr(buildName.length - maxLength + ellipsisLength, maxLength - ellipsisLength)}`;
    }

    private initializeState(): void {
        this.state = {
            branchName: "",
            builds: [],
            environmentsInfo: [],
            deploymentsMap: {},
            testResultsMap: {},
            lastColumnMap: {},
            buildDefinitionInfo: undefined,
        }
    }
}

VssComponent.register(DeploymentStatusWidget.componentType, DeploymentStatusWidget);